import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import { Vendor, VendorEdit, VendorRating, VendorRep } from '../types';
import {
  vendorsApi,
  type RateVendorInput,
  type RepPatch,
  type SuggestEditInput,
  type SuggestRepInput,
  type SuggestVendorInput,
  type VendorDirectory,
  type VendorPatch,
  type VendorSearchResult,
  type VendorUploadResult,
} from '../api/vendors';
import { useAuthStore } from './authStore';
import { createPrefixedLogger } from '../utils/logger';
import type { VendorUploadPlan } from '../utils/vendorImport';

const log = createPrefixedLogger('vendorsStore');

// Module-level handles, same reason as areaContactsStore: a store's own state
// is no place to keep Unsubscribe closures across StrictMode double-invokes.
let unsubs: Unsubscribe[] = [];
let startKey: string | null = null;
let approvedFeed: VendorDirectory | null = null;
let mineFeed: VendorDirectory | null = null;
let visibleRatings: VendorRating[] = [];
let myRatings: VendorRating[] = [];

function byId<T extends { id: string }>(first: T[], second: T[]): T[] {
  const map = new Map<string, T>();
  for (const item of first) map.set(item.id, item);
  for (const item of second) map.set(item.id, item); // second wins
  return Array.from(map.values());
}

/** Approved docs plus the account's own, deduped by id. Mine wins, because
 *  it can be newer (a just-reviewed suggestion) or hold a rejected status. */
export function mergeDirectoryFeeds(
  approved: VendorDirectory,
  mine: VendorDirectory
): VendorDirectory {
  const vendors = byId(approved.vendors, mine.vendors);
  const reps = byId(approved.reps, mine.reps);
  vendors.sort((a, b) =>
    a.organization.localeCompare(b.organization, undefined, { sensitivity: 'base' })
  );
  reps.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return { vendors, reps };
}

/** Suggested vendors, plus suggested reps whose vendor is already approved,
 *  plus suggested edits. A suggested rep on a suggested vendor is reviewed
 *  with its vendor. */
export function pendingReviewCount(
  vendors: Vendor[],
  reps: VendorRep[],
  edits: VendorEdit[] = []
): number {
  const approved = new Set(vendors.filter((v) => v.status === 'approved').map((v) => v.id));
  return (
    vendors.filter((v) => v.status === 'suggested').length +
    reps.filter((r) => r.status === 'suggested' && approved.has(r.vendorId)).length +
    edits.filter((e) => e.status === 'suggested').length
  );
}

interface VendorsState {
  vendors: Vendor[];
  reps: VendorRep[];
  /** Moderators: edits waiting for review. Everyone else: their own edits. */
  edits: VendorEdit[];
  /** Moderators: every rating. Everyone else: ratings not hidden, plus
   *  their own. */
  ratings: VendorRating[];
  /** False until every listener has delivered (or failed). Edits and
   *  ratings don't hold it back; they fill in when they arrive. */
  isLoaded: boolean;
  error: string | null;

  /** Starts the listeners, lazily from the UI. Moderators get everything;
   *  everyone else gets approved docs plus their own suggestions. Safe to
   *  call twice with the same arguments. */
  start: (uid: string, isModerator: boolean) => void;
  stop: () => void;

  suggestVendor: (
    input: SuggestVendorInput
  ) => Promise<{ vendorId: string; repId?: string } | null>;
  suggestRep: (vendorId: string, rep: SuggestRepInput) => Promise<string | null>;

  /** Moderator. `repIds` defaults to the vendor's reps still waiting. */
  reviewVendor: (
    vendorId: string,
    status: 'approved' | 'rejected',
    repIds?: string[]
  ) => Promise<void>;
  reviewRep: (repId: string, status: 'approved' | 'rejected') => Promise<void>;
  /** Moderator: web search for one kind of work near the area (Nov 2026).
   *  Found companies arrive as suggestions through the subscription. */
  findCandidates: (areaId: string, category: string) => Promise<VendorSearchResult>;
  updateVendor: (vendorId: string, patch: VendorPatch) => Promise<void>;
  updateRep: (repId: string, patch: RepPatch) => Promise<void>;
  /** Moderator. `repIds` defaults to every rep of the vendor. */
  deleteVendor: (vendorId: string, repIds?: string[]) => Promise<void>;
  applyVendorUpload: (plan: VendorUploadPlan) => Promise<VendorUploadResult>;

  /** Throws on failure, so the form can say so. */
  suggestEdit: (input: SuggestEditInput) => Promise<string>;
  withdrawEdit: (editId: string) => Promise<void>;
  /** Moderator. Approving applies the change in the same batch. */
  reviewEdit: (edit: VendorEdit, status: 'approved' | 'rejected') => Promise<void>;

  /** A new rating, or new stars and note on the account's own one. */
  rateVendor: (input: RateVendorInput) => Promise<void>;
  deleteRating: (ratingId: string) => Promise<void>;
  /** Moderator. */
  setRatingHidden: (ratingId: string, hidden: boolean) => Promise<void>;

  reset: () => void;
}

/** Selector: `useVendorsStore(selectPendingReviewCount)`. A number, so it
 *  never re-renders on an unrelated vendor change. */
export const selectPendingReviewCount = (
  s: Pick<VendorsState, 'vendors' | 'reps'> & Partial<Pick<VendorsState, 'edits'>>
): number => pendingReviewCount(s.vendors, s.reps, s.edits);

function requireUid(): string {
  const user = useAuthStore.getState().user;
  if (!user) throw new Error('Sign in first.');
  return user.id;
}

export const useVendorsStore = create<VendorsState>((set, get) => {
  const fail = (error: Error) => {
    log.warn('listener failed:', error);
    set({ isLoaded: true, error: error.message });
  };

  // Edits and ratings are extras on the directory: a failed listener is
  // logged, never shown as a directory error.
  const quiet = (which: string) => (error: Error) => log.warn(`${which} listener failed:`, error);

  return {
    vendors: [],
    reps: [],
    edits: [],
    ratings: [],
    isLoaded: false,
    error: null,

    start: (uid, isModerator) => {
      const key = `${uid}:${isModerator ? 'mod' : 'user'}`;
      if (startKey === key) return;
      get().stop();
      startKey = key;
      set({ isLoaded: false, error: null });

      if (isModerator) {
        unsubs = [
          vendorsApi.subscribeAllForModerator(
            ({ vendors, reps }) => set({ vendors, reps, isLoaded: true, error: null }),
            fail
          ),
          vendorsApi.subscribeEditsForModerator((edits) => set({ edits }), quiet('edits')),
          vendorsApi.subscribeAllRatingsForModerator(
            (ratings) => set({ ratings }),
            quiet('ratings')
          ),
        ];
        return;
      }

      const publishRatings = () => {
        const mine = new Set(myRatings.map((r) => r.id));
        set({ ratings: [...visibleRatings.filter((r) => !mine.has(r.id)), ...myRatings] });
      };

      const publish = () => {
        // Wait for both feeds so a signed-in user's first paint isn't half a list.
        if (!approvedFeed || !mineFeed) return;
        const { vendors, reps } = mergeDirectoryFeeds(approvedFeed, mineFeed);
        set({ vendors, reps, isLoaded: true, error: null });
      };
      unsubs = [
        vendorsApi.subscribeApproved((dir) => {
          approvedFeed = dir;
          publish();
        }, fail),
        vendorsApi.subscribeMine(
          uid,
          (dir) => {
            mineFeed = dir;
            publish();
          },
          fail
        ),
        vendorsApi.subscribeMyEdits(uid, (edits) => set({ edits }), quiet('edits')),
        vendorsApi.subscribeVisibleRatings((ratings) => {
          visibleRatings = ratings;
          publishRatings();
        }, quiet('ratings')),
        vendorsApi.subscribeMyRatings(
          uid,
          (ratings) => {
            myRatings = ratings;
            publishRatings();
          },
          quiet('my ratings')
        ),
      ];
    },

    stop: () => {
      for (const unsub of unsubs) unsub();
      unsubs = [];
      startKey = null;
      approvedFeed = null;
      mineFeed = null;
      visibleRatings = [];
      myRatings = [];
    },

    suggestVendor: async (input) => {
      try {
        return await vendorsApi.suggestVendor(requireUid(), input);
      } catch (error) {
        log.warn('suggestVendor failed:', error);
        set({ error: (error as Error).message });
        return null;
      }
    },

    suggestRep: async (vendorId, rep) => {
      try {
        return await vendorsApi.suggestRep(requireUid(), vendorId, rep);
      } catch (error) {
        log.warn('suggestRep failed:', error);
        set({ error: (error as Error).message });
        return null;
      }
    },

    reviewVendor: async (vendorId, status, repIds) => {
      const ids =
        repIds ??
        get()
          .reps.filter((r) => r.vendorId === vendorId && r.status === 'suggested')
          .map((r) => r.id);
      await vendorsApi.reviewVendor(requireUid(), vendorId, status, ids);
    },

    reviewRep: async (repId, status) => {
      await vendorsApi.reviewRep(requireUid(), repId, status);
    },

    findCandidates: (areaId, category) => vendorsApi.findCandidates(areaId, category),

    updateVendor: async (vendorId, patch) => {
      await vendorsApi.updateVendor(vendorId, patch);
    },

    updateRep: async (repId, patch) => {
      await vendorsApi.updateRep(repId, patch);
    },

    deleteVendor: async (vendorId, repIds) => {
      const ids =
        repIds ??
        get()
          .reps.filter((r) => r.vendorId === vendorId)
          .map((r) => r.id);
      await vendorsApi.deleteVendor(vendorId, ids);
    },

    applyVendorUpload: async (plan) => vendorsApi.applyVendorUpload(requireUid(), plan),

    suggestEdit: async (input) => vendorsApi.suggestEdit(requireUid(), input),

    withdrawEdit: async (editId) => {
      await vendorsApi.withdrawEdit(editId);
    },

    reviewEdit: async (edit, status) => {
      await vendorsApi.reviewEdit(requireUid(), edit, status);
    },

    rateVendor: async (input) => {
      const uid = requireUid();
      const existing = get().ratings.find(
        (r) => r.vendorId === input.vendorId && r.createdBy === uid
      );
      if (existing) await vendorsApi.updateRating(existing.id, input.stars, input.note ?? '');
      else await vendorsApi.rateVendor(uid, input);
    },

    deleteRating: async (ratingId) => {
      await vendorsApi.deleteRating(ratingId);
    },

    setRatingHidden: async (ratingId, hidden) => {
      await vendorsApi.setRatingHidden(requireUid(), ratingId, hidden);
    },

    reset: () => {
      get().stop();
      set({ vendors: [], reps: [], edits: [], ratings: [], isLoaded: false, error: null });
    },
  };
});
